/**
 * src/mcp/components/MCPServerPanel.jsx
 *
 * Relocated from src/components/MCPServerPanel.jsx — grouped with the rest
 * of the MCP chat feature (folder-structure cleanup). Content unchanged.
 *
 * Panel for managing MCP server connections with ENV support
 */
import { useState } from 'react';
import { SkeletonList } from '../../shared/ui';

export default function MCPServerPanel({ servers, isLoading, onConnect, onDisconnect, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [serverName, setServerName] = useState('');
  const [serverCommand, setServerCommand] = useState('');
  const [envVars, setEnvVars] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [showPresets, setShowPresets] = useState(true);

  // Preset server configurations
  const presets = [
    {
      name: 'filesystem',
      label: '📁 Filesystem',
      command: 'npx -y @modelcontextprotocol/server-filesystem C:\\temp',
      description: 'Read/write/search files',
      tools: ['read_file', 'write_file', 'list_directory', 'search_files']
    },
    {
      name: 'memory',
      label: '🧠 Memory',
      command: 'npx -y @modelcontextprotocol/server-memory',
      description: 'Store and retrieve knowledge',
      tools: ['store_memory', 'retrieve_memory', 'list_memories']
    },
    {
      name: 'fetch',
      label: '🌐 Fetch',
      command: 'npx -y @modelcontextprotocol/server-fetch',
      description: 'Fetch web content',
      tools: ['fetch_url', 'fetch_html']
    },
    {
      name: 'everything',
      label: '🎯 Everything',
      command: 'npx -y @modelcontextprotocol/server-everything',
      description: 'Demo server with all tool types',
      tools: ['echo', 'add', 'longRunningOperation', 'sampleLLM']
    },
    {
  name: 'jira',
  label: '🎫 Jira',
  command: 'npx -y @anthropic/mcp-server-atlassian',
  description: 'Jira issue tracking',
  requiresEnv: 'ATLASSIAN_API_TOKEN',
  envPlaceholder: 'ATLASSIAN_SITE_URL=https://yourcompany.atlassian.net\nATLASSIAN_USER_EMAIL=your.email@company.com\nATLASSIAN_API_TOKEN=your_token_here',
  tools: ['search_issues', 'create_issue', 'get_issue', 'update_issue']
},
    {
      name: 'thinking',
      label: '💭 Sequential Thinking',
      command: 'npx -y @modelcontextprotocol/server-sequential-thinking',
      description: 'Step-by-step reasoning',
      tools: ['think_step_by_step']
    },
    {
      name: 'sqlite',
      label: '🗄️ SQLite',
      command: 'npx -y @modelcontextprotocol/server-sqlite --db-path C:\\path\\to\\db.sqlite',
      description: 'SQLite database queries',
      tools: ['query', 'execute']
    },
    {
      name: 'git',
      label: '📦 Git',
      command: 'npx -y @modelcontextprotocol/server-git --repository C:\\path\\to\\repo',
      description: 'Git operations',
      tools: ['git_status', 'git_log', 'git_diff']
    },
    {
      name: 'puppeteer',
      label: '🎭 Puppeteer',
      command: 'npx -y @modelcontextprotocol/server-puppeteer',
      description: 'Browser automation',
      tools: ['navigate', 'screenshot', 'click', 'fill']
    },
    {
      name: 'brave',
      label: '🔍 Brave Search',
      command: 'npx -y @modelcontextprotocol/server-brave-search',
      description: 'Web search (needs API key)',
      requiresEnv: 'BRAVE_API_KEY',
      envPlaceholder: 'BRAVE_API_KEY=your_api_key_here',
      tools: ['search']
    },
    {
      name: 'github',
      label: '🐙 GitHub',
      command: 'npx -y @modelcontextprotocol/server-github',
      description: 'GitHub API access (needs token)',
      requiresEnv: 'GITHUB_PERSONAL_ACCESS_TOKEN',
      envPlaceholder: 'GITHUB_PERSONAL_ACCESS_TOKEN=ghp_xxxx',
      tools: ['search_repos', 'get_file', 'create_issue']
    },
  ];

  const parseEnvVars = (envString) => {
    if (!envString.trim()) return null;

    const env = {};
    const lines = envString.split('\n');

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#')) continue;

      const eqIndex = trimmed.indexOf('=');
      if (eqIndex > 0) {
        const key = trimmed.substring(0, eqIndex).trim();
        const value = trimmed.substring(eqIndex + 1).trim();
        env[key] = value;
      }
    }

    return Object.keys(env).length > 0 ? env : null;
  };

  const handleConnect = async (e) => {
    e.preventDefault();
    if (!serverName || !serverCommand) return;

    setConnecting(true);
    setError(null);

    try {
      const command = serverCommand.split(' ').filter(Boolean);
      const env = parseEnvVars(envVars);
      await onConnect(serverName, command, env);
      setServerName('');
      setServerCommand('');
      setEnvVars('');
      setShowForm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setConnecting(false);
    }
  };

  const handlePresetClick = (preset) => {
    setServerName(preset.name);
    setServerCommand(preset.command);
    setEnvVars(preset.envPlaceholder || '');
    setShowForm(true);
    setShowPresets(false);
  };

  const handlePresetConnect = async (preset) => {
    if (preset.requiresEnv) {
      handlePresetClick(preset);
      return;
    }

    setConnecting(true);
    setError(null);

    try {
      const command = preset.command.split(' ').filter(Boolean);
      await onConnect(preset.name, command);
    } catch (err) {
      setError(err.message);
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div className="mcp-server-panel">
      <div className="mcp-panel-header">
        <h3>MCP Servers</h3>
        <div className="mcp-panel-actions">
          <button onClick={onRefresh} title="Refresh" className="mcp-icon-btn">↻</button>
          <button
            onClick={() => { setShowForm(!showForm); setShowPresets(false); }}
            className="mcp-icon-btn"
            title="Custom server"
          >
            {showForm ? '✕' : '+'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mcp-error">
          <span className="mcp-error-icon">⚠️</span>
          {error}
        </div>
      )}

      {/* Connected Servers */}
      <div className="mcp-server-list">
        {isLoading ? (
          <SkeletonList rows={3} />
        ) : servers.length === 0 ? (
          <div className="mcp-no-servers">
            <span>No servers connected</span>
            <small>Connect a server below to enable tools</small>
          </div>
        ) : (
          servers.map(server => (
            <div key={server.name} className="mcp-server-card">
              <div className="mcp-server-info">
                <div className="mcp-server-name-row">
                  <span className="mcp-status-dot"></span>
                  <span className="mcp-server-name">{server.name}</span>
                </div>
                <span className="mcp-server-tools">{server.tools} tools available</span>
              </div>
              <button
                className="mcp-disconnect-btn"
                onClick={() => onDisconnect(server.name)}
                title="Disconnect"
              >
                ✕
              </button>
            </div>
          ))
        )}
      </div>

      {/* Quick Connect Presets */}
      {!showForm && (
        <div className="mcp-presets-section">
          <div
            className="mcp-presets-header"
            onClick={() => setShowPresets(!showPresets)}
          >
            <span>{showPresets ? '▼' : '▶'} Quick Connect</span>
            <small>{presets.length} servers</small>
          </div>

          {showPresets && (
            <div className="mcp-presets-grid">
              {presets.map(preset => (
                <div
                  key={preset.name}
                  className={`mcp-preset-card ${servers.some(s => s.name === preset.name) ? 'connected' : ''}`}
                  onClick={() => !servers.some(s => s.name === preset.name) && handlePresetConnect(preset)}
                  title={preset.description}
                >
                  <div className="mcp-preset-label">{preset.label}</div>
                  <div className="mcp-preset-desc">{preset.description}</div>
                  {preset.requiresEnv && (
                    <div className="mcp-preset-env">🔑 Needs API key</div>
                  )}
                  {servers.some(s => s.name === preset.name) && (
                    <div className="mcp-preset-connected">✓ Connected</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Connection Form */}
      {showForm && (
        <form className="mcp-connect-form" onSubmit={handleConnect}>
          <div className="mcp-form-group">
            <label>Server Name</label>
            <input
              type="text"
              value={serverName}
              onChange={(e) => setServerName(e.target.value)}
              placeholder="e.g., filesystem"
            />
          </div>

          <div className="mcp-form-group">
            <label>Command</label>
            <input
              type="text"
              value={serverCommand}
              onChange={(e) => setServerCommand(e.target.value)}
              placeholder="npx -y @modelcontextprotocol/server-..."
            />
            <small className="mcp-form-hint">Full command to start the MCP server</small>
          </div>

          <div className="mcp-form-group">
            <label>Environment Variables <span className="mcp-optional">(optional)</span></label>
            <textarea
              value={envVars}
              onChange={(e) => setEnvVars(e.target.value)}
              placeholder="KEY=value&#10;ANOTHER_KEY=another_value"
              rows={3}
            />
            <small className="mcp-form-hint">One per line: KEY=value</small>
          </div>

          <div className="mcp-form-actions">
            <button
              type="button"
              className="mcp-btn-secondary"
              onClick={() => { setShowForm(false); setShowPresets(true); }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="mcp-btn-primary"
              disabled={connecting || !serverName || !serverCommand}
            >
              {connecting ? 'Connecting...' : 'Connect'}
            </button>
          </div>
        </form>
      )}

      <style>{`
        .mcp-server-panel {
          padding: 16px;
          border-bottom: 1px solid #2a2a2e;
        }

        .mcp-panel-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .mcp-panel-header h3 {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          color: #e4e4e7;
        }

        .mcp-panel-actions {
          display: flex;
          gap: 4px;
        }

        .mcp-icon-btn {
          background: transparent;
          border: 1px solid #2a2a2e;
          color: #a1a1aa;
          width: 28px;
          height: 28px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .mcp-icon-btn:hover {
          border-color: #10b981;
          color: #10b981;
        }

        .mcp-error {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          color: #fca5a5;
          padding: 10px 12px;
          border-radius: 6px;
          font-size: 12px;
          margin-bottom: 12px;
          display: flex;
          align-items: flex-start;
          gap: 8px;
        }

        .mcp-error-icon {
          flex-shrink: 0;
        }

        .mcp-server-list {
          margin-bottom: 12px;
        }

        .mcp-no-servers {
          color: #71717a;
          font-size: 13px;
          text-align: center;
          padding: 20px 16px;
          background: #18181b;
          border-radius: 8px;
          border: 1px dashed #2a2a2e;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .mcp-no-servers small {
          font-size: 11px;
          color: #52525b;
        }

        .mcp-server-card {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #1c1c1f;
          border: 1px solid #2a2a2e;
          border-radius: 8px;
          padding: 12px;
          margin-bottom: 6px;
          transition: border-color 0.2s;
        }

        .mcp-server-card:hover {
          border-color: #3a3a3e;
        }

        .mcp-server-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .mcp-server-name-row {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .mcp-status-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #10b981;
          box-shadow: 0 0 6px rgba(16, 185, 129, 0.5);
        }

        .mcp-server-name {
          font-size: 13px;
          font-weight: 500;
          color: #10b981;
          font-family: 'JetBrains Mono', monospace;
        }

        .mcp-server-tools {
          font-size: 11px;
          color: #71717a;
          margin-left: 16px;
        }

        .mcp-disconnect-btn {
          background: transparent;
          border: none;
          color: #71717a;
          cursor: pointer;
          padding: 6px 10px;
          font-size: 12px;
          border-radius: 4px;
          transition: all 0.2s;
        }

        .mcp-disconnect-btn:hover {
          color: #ef4444;
          background: rgba(239, 68, 68, 0.1);
        }

        /* Presets Section */
        .mcp-presets-section {
          margin-top: 12px;
        }

        .mcp-presets-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          cursor: pointer;
          user-select: none;
          color: #71717a;
          font-size: 12px;
        }

        .mcp-presets-header:hover {
          color: #a1a1aa;
        }

        .mcp-presets-header small {
          font-size: 11px;
          color: #52525b;
        }

        .mcp-presets-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
          margin-top: 8px;
        }

        .mcp-preset-card {
          background: #18181b;
          border: 1px solid #2a2a2e;
          border-radius: 8px;
          padding: 10px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .mcp-preset-card:hover:not(.connected) {
          border-color: #10b981;
          background: rgba(16, 185, 129, 0.05);
        }

        .mcp-preset-card.connected {
          opacity: 0.6;
          cursor: default;
        }

        .mcp-preset-label {
          font-size: 12px;
          font-weight: 500;
          color: #e4e4e7;
          margin-bottom: 4px;
        }

        .mcp-preset-desc {
          font-size: 10px;
          color: #71717a;
          line-height: 1.3;
        }

        .mcp-preset-env {
          font-size: 9px;
          color: #f59e0b;
          margin-top: 4px;
        }

        .mcp-preset-connected {
          font-size: 10px;
          color: #10b981;
          margin-top: 4px;
        }

        /* Connection Form */
        .mcp-connect-form {
          background: #18181b;
          border: 1px solid #2a2a2e;
          border-radius: 8px;
          padding: 16px;
          margin-top: 12px;
        }

        .mcp-form-group {
          margin-bottom: 14px;
        }

        .mcp-form-group label {
          display: block;
          font-size: 11px;
          font-weight: 500;
          color: #a1a1aa;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          margin-bottom: 6px;
        }

        .mcp-optional {
          color: #52525b;
          font-weight: 400;
          text-transform: none;
        }

        .mcp-form-group input,
        .mcp-form-group textarea {
          width: 100%;
          background: #0a0a0b;
          border: 1px solid #2a2a2e;
          color: #e4e4e7;
          padding: 10px 12px;
          border-radius: 6px;
          font-size: 13px;
          font-family: 'JetBrains Mono', monospace;
          transition: border-color 0.2s;
        }

        .mcp-form-group textarea {
          resize: vertical;
          min-height: 60px;
        }

        .mcp-form-group input:focus,
        .mcp-form-group textarea:focus {
          outline: none;
          border-color: #10b981;
        }

        .mcp-form-group input::placeholder,
        .mcp-form-group textarea::placeholder {
          color: #52525b;
        }

        .mcp-form-hint {
          display: block;
          font-size: 10px;
          color: #52525b;
          margin-top: 4px;
        }

        .mcp-form-actions {
          display: flex;
          gap: 8px;
          margin-top: 16px;
        }

        .mcp-btn-primary,
        .mcp-btn-secondary {
          flex: 1;
          padding: 10px 16px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .mcp-btn-primary {
          background: #10b981;
          border: none;
          color: #000;
        }

        .mcp-btn-primary:hover:not(:disabled) {
          background: #059669;
        }

        .mcp-btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .mcp-btn-secondary {
          background: transparent;
          border: 1px solid #2a2a2e;
          color: #a1a1aa;
        }

        .mcp-btn-secondary:hover {
          border-color: #3a3a3e;
          color: #e4e4e7;
        }
      `}</style>
    </div>
  );
}
